import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { Logger } from '../../shared/services/logger.service';

interface SessionState {
  orgPid: string | null;
  orgSlug: string | null;
  redirectTo: string | null;
  sessionId: string | null;

  setSession: (
    orgPid: string,
    orgSlug: string,
    redirectTo?: string | null,
    sessionId?: string
  ) => void;
  clearSession: () => void;
  setOrgPid: (orgPid: string) => void;
  setSessionId: (sessionId: string) => void;
  getOrCreateSessionId: () => string;
}

const initialSessionState = {
  orgPid: null,
  orgSlug: null,
  redirectTo: null,
  sessionId: null,
};

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      ...initialSessionState,

      setSession: (orgPid, orgSlug, redirectTo = null, sessionId) => {
        // Reuse the existing per-tab session ID (the socket already connected
        // with it) instead of generating a fresh one on each login. Only
        // create a new ID when neither the caller nor the store has one yet.
        const finalSessionId = sessionId || get().getOrCreateSessionId();

        set({
          orgPid,
          orgSlug,
          redirectTo,
          sessionId: finalSessionId,
        });
      },

      clearSession: () => {
        Logger.debug('[SessionStore] Clearing session');
        try {
          sessionStorage.removeItem('nexgen-session-id');
        } catch (e) {
          Logger.error('[SessionStore] Failed to remove session ID from sessionStorage:', e);
        }
        set({ ...initialSessionState });
      },

      setOrgPid: (orgPid: string) => set({ orgPid }),

      // sessionId is backed by sessionStorage so each tab is a distinct session.
      // The rest of the store stays in localStorage (shared across tabs).
      setSessionId: (sessionId: string) => {
        Logger.debug('[SessionStore] Setting session ID:', sessionId);
        try {
          sessionStorage.setItem('nexgen-session-id', sessionId);
        } catch (e) {
          Logger.error('[SessionStore] Failed to write session ID to sessionStorage:', e);
        }
        set({ sessionId });
      },

      getOrCreateSessionId: () => {
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

        try {
          const stored = sessionStorage.getItem('nexgen-session-id');
          if (stored && uuidRegex.test(stored)) {
            if (get().sessionId !== stored) set({ sessionId: stored });
            return stored;
          }
        } catch (e) {
          Logger.error('[SessionStore] Failed to read session ID from sessionStorage:', e);
        }

        const newSessionId = uuidv4();
        Logger.debug('[SessionStore] Generated new session ID:', newSessionId);
        try {
          sessionStorage.setItem('nexgen-session-id', newSessionId);
        } catch (e) {
          Logger.error('[SessionStore] Failed to write session ID to sessionStorage:', e);
        }
        set({ sessionId: newSessionId });
        return newSessionId;
      },
    }),
    {
      name: 'session-storage',
      partialize: (state) => ({
        orgPid: state.orgPid,
        orgSlug: state.orgSlug,
        redirectTo: state.redirectTo,
      }),
    }
  )
);
